import {
  cloneElement,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

type Side = 'top' | 'bottom' | 'left' | 'right';

/**
 * Light tooltip, and the canonical disabled-reason wrapper — reference §14.
 *
 * `disabledReason` is the important half: a `disabled` button fires no pointer events, so a
 * plain tooltip on it never appears. Supplying a reason wraps the child in a focusable
 * `<span tabIndex={0}>` so the explanation is reachable by both hover and keyboard. That is
 * what makes "every disabled control explains why" (§8) actually achievable.
 */
export function Tooltip({
  label,
  children,
  delay = 250,
  side = 'top',
  disabled = false,
  disabledReason = null,
  block = false,
}: {
  label?: ReactNode;
  /** A single element. It is cloned to receive `aria-describedby`. */
  children: ReactElement<{ 'aria-describedby'?: string }>;
  delay?: number;
  side?: Side;
  disabled?: boolean;
  disabledReason?: string | null;
  block?: boolean;
}) {
  const id = useId();
  const [visible, setVisible] = useState(false);
  const [rect, setRect] = useState<{ top: number; left: number } | null>(null);

  const anchorRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const timer = useRef<number | null>(null);

  // A reason wins over the label — it is the more specific thing to say.
  const content = disabledReason?.trim() ? disabledReason : label;
  const inert = disabled || !content;

  const show = useCallback(
    (immediate: boolean) => {
      if (inert) return;

      if (timer.current) window.clearTimeout(timer.current);

      if (immediate) {
        setVisible(true);
      } else {
        timer.current = window.setTimeout(() => setVisible(true), delay);
      }
    },
    [inert, delay],
  );

  const hide = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current);
    setVisible(false);
  }, []);

  useLayoutEffect(() => {
    if (!visible || !anchorRef.current) return;

    const anchor = anchorRef.current.getBoundingClientRect();
    const tip = tipRef.current?.getBoundingClientRect();
    const width = tip?.width ?? 160;
    const height = tip?.height ?? 28;
    const gap = 6;

    // Auto-flip when the preferred side would leave the viewport.
    let resolved: Side = side;

    if (side === 'top' && anchor.top - height - gap < 0) resolved = 'bottom';
    if (side === 'bottom' && anchor.bottom + height + gap > window.innerHeight) resolved = 'top';
    if (side === 'left' && anchor.left - width - gap < 0) resolved = 'right';
    if (side === 'right' && anchor.right + width + gap > window.innerWidth) resolved = 'left';

    const positions: Record<Side, { top: number; left: number }> = {
      top: { top: anchor.top - height - gap, left: anchor.left + anchor.width / 2 - width / 2 },
      bottom: { top: anchor.bottom + gap, left: anchor.left + anchor.width / 2 - width / 2 },
      left: { top: anchor.top + anchor.height / 2 - height / 2, left: anchor.left - width - gap },
      right: { top: anchor.top + anchor.height / 2 - height / 2, left: anchor.right + gap },
    };

    const next = positions[resolved];

    setRect({
      top: Math.max(4, next.top),
      left: Math.min(Math.max(4, next.left), window.innerWidth - width - 4),
    });
  }, [visible, side]);

  const trigger = cloneElement(children, { 'aria-describedby': visible ? id : undefined });

  // Without the wrapper, a disabled child swallows the events that would reveal the reason.
  const needsFocusableWrapper = Boolean(disabledReason?.trim());

  return (
    <>
      <span
        ref={anchorRef}
        className={block ? 'block w-full' : 'inline-flex'}
        tabIndex={needsFocusableWrapper ? 0 : undefined}
        onMouseEnter={() => show(false)}
        onMouseLeave={hide}
        onFocusCapture={() => show(true)}
        onBlurCapture={hide}
      >
        {trigger}
      </span>

      {visible && rect
        ? createPortal(
            <span
              ref={tipRef}
              id={id}
              role="tooltip"
              style={{ position: 'fixed', top: rect.top, left: rect.left, zIndex: 2000 }}
              className="pointer-events-none block w-max max-w-64 rounded-md bg-white px-2 py-1 text-xs text-slate-600 shadow-raised ring-1 ring-slate-200"
            >
              {content}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
